import { useMemo } from 'react';

import {
  useGetCurrentDepartmentTree,
  useGetCurrentOrganization,
} from '@/api/trafikportalen';
import { checkUserPermissions } from '@/components/Auth/checkUserPermissions';
import {
  isStudentCollectionType,
  type StudentCollectionType,
} from '@/helpers/collectionTypes';
import {
  hasDepartmentAccess,
  hasDepartmentWriteAccess,
} from '@/helpers/departmentPermissions';
import { findDepartmentByCollectionType } from '@/hooks/useGetStudentCollection';
import { useAuth } from '@/stores/auth';

interface UseAccessibleStudentCategoriesProps {
  requireWriteAccess?: boolean;
}

/**
 * Returns the list of student collection types the current user has
 * access to, intersecting the organization's enabled collection types
 * with the user's department permissions. Organization-level users
 * implicitly have access to every enabled category, provided their
 * organization-level permission satisfies the requested access level
 * (viewers cannot satisfy a write-access requirement).
 */
export const useAccessibleStudentCategories = ({
  requireWriteAccess = false,
}: UseAccessibleStudentCategoriesProps = {}): StudentCollectionType[] => {
  const { userProfile } = useAuth();
  const isSysadmin = !userProfile?.organization_id;
  const isOrgEditor = checkUserPermissions({
    permission: 'organization:editor',
  });
  const isOrgViewer = checkUserPermissions({
    permission: 'organization:viewer',
  });
  const hasOrgLevelAccess = requireWriteAccess ? isOrgEditor : isOrgViewer;

  const { data: organization } = useGetCurrentOrganization({
    query: { enabled: !isSysadmin },
  });
  const { data: departmentTree } = useGetCurrentDepartmentTree({
    query: { enabled: !isSysadmin },
  });

  return useMemo(() => {
    if (isSysadmin || !organization || !departmentTree) return [];

    const enabledTypes = (organization.enabled_collection_types ?? []).filter(
      isStudentCollectionType,
    );

    return enabledTypes.filter((type) => {
      if (hasOrgLevelAccess) return true;
      const department = findDepartmentByCollectionType(departmentTree, type);
      if (!department) return false;
      return requireWriteAccess
        ? hasDepartmentWriteAccess(department.id)
        : hasDepartmentAccess(department.id);
    });
  }, [
    isSysadmin,
    hasOrgLevelAccess,
    organization,
    departmentTree,
    requireWriteAccess,
  ]);
};
